import clsx from 'clsx';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SizeChartDisplayResult } from '@vestico/model';

import { SizeRecommendationTabSizeChart, Spinner, SpinnerPositioning, SpinnerSize } from '@vestico/shared';

import style from './SizeRecommendationTabSelect.module.css';

export interface SizeRecommendationTabSelectProps {
  sizeChart: SizeChartDisplayResult | undefined;
  sizeRecTabContent: ReactNode;
  showSizeRecTab: boolean;
}

export const SizeRecommendationTabSelect = ({ showSizeRecTab, sizeRecTabContent, sizeChart }: SizeRecommendationTabSelectProps) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);

  const onSelectTab = useCallback(
    (index: number) => () => {
      setSelectedTab(index);
    },
    []
  );

  const sizeChartHeaderTitle = useMemo(() => t('LOC_SIZE_REC_TAB_SIZE_CHART'), [t]);

  const selectorContent = useMemo(() => {
    if (!sizeChart || !showSizeRecTab) {
      return undefined;
    }

    return (
      <div>
        <div className={clsx(style.tabWrapper)}>
          <button type="button" className={clsx(style.tab, { [style.selected]: selectedTab === 0 })} onClick={onSelectTab(0)}>
            {t('LOC_WIDGET_INFO_DESCRIPTION_SIZE_REC')}
          </button>
          <button type="button" className={clsx(style.tab, { [style.selected]: selectedTab === 1 })} onClick={onSelectTab(1)}>
            {sizeChartHeaderTitle}
          </button>
        </div>
        <div className={clsx(style.line)} style={{ transform: `translateX(${selectedTab === 1 ? '100%' : '0%'})` }} />
      </div>
    );
  }, [onSelectTab, selectedTab, showSizeRecTab, sizeChart, sizeChartHeaderTitle, t]);

  const content = useMemo(() => {
    if (!sizeRecTabContent && showSizeRecTab) {
      return <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.flex} />;
    }

    return (selectedTab === 1 || !showSizeRecTab) && sizeChart ? (
      <SizeRecommendationTabSizeChart sizeChart={sizeChart} title={!showSizeRecTab ? sizeChartHeaderTitle : undefined} />
    ) : (
      sizeRecTabContent
    );
  }, [selectedTab, showSizeRecTab, sizeChart, sizeChartHeaderTitle, sizeRecTabContent]);

  return (
    // eslint-disable-next-line react/no-unknown-property
    <div className={clsx(style.root)} body-scroll-lock-ignore="true">
      {selectorContent}
      <div className={clsx(style.content)}>{content}</div>
    </div>
  );
};
